import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { DeepLinkSource } from '../schemas/shop.schema';
import { parseSource } from '../services/deep-link.service';
import { rememberSource } from '../services/source-attribution.service';

/**
 * Read `?src=` off the current URL, remember it for the rest of the visit, and
 * return it.
 *
 * ── WHY A HOOK AND NOT `parseSource(searchParams.get('src'))` ──────────────
 * Because observing the source and REPORTING it are two halves of one job, and
 * splitting them is how the reporting half went missing: `?src=ptc` reached
 * `SourceBadge` and the quiz-skip, but never reached the Accounts prospect. A page
 * that reads the source now cannot forget to carry it.
 *
 * `parseSource` stays a pure function with no side effects — the write lives here,
 * in an effect, so nothing is stored during render.
 *
 * The store applies "first non-organic touch wins", so calling this on several
 * pages is safe and order-independent (see `source-attribution.service.ts`).
 */
export function useDeepLinkSource(): DeepLinkSource {
  const [searchParams] = useSearchParams();
  const source = parseSource(searchParams.get('src'));

  useEffect(() => {
    rememberSource(source);
  }, [source]);

  return source;
}
